import { CheckIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { Form, Formik, FormikConfig } from 'formik';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../../components/Button';
import { UserContext } from '../../../contexts/UserContext';
import { PROJECT_COMMENTS } from '../../../graphql/project';
import { useFormatDate } from '../../../hooks/useFormatDate';
import useOpen from '../../../hooks/useOpen';
import { useCustomSelector } from '../../../redux/store';
import { T, useT } from '../../../translation/src';
import { Comment, Project, ProjectAccessLevel, ProjectByIdQuery, useProjectAddCommentMutation, useProjectCommentsByObjectIdQuery, useProjectEditCommentMutation } from '../../../types/graphqlTypes';
import { ProjectAccessLevelContext } from '../../../contexts/ProjectAccessLevelContext';
import { ProjectDeleteCommentModal } from '../../project/ProjectDeleteCommentModal';
import { CommentInput, CommentInputHandle } from '../../../components/Comment';
import DOMPurify from 'dompurify';
import { useVirtualizer } from '@tanstack/react-virtual';
import { NetworkStatus } from '@apollo/client';

interface FormValues {
  comment: string;
}

const useTranslations = () => {
  const commentPlaceholder = useT('new comment...', { swc: true });
  return { commentPlaceholder };
};

interface CommentsProps {
  project: Pick<Project, 'id'> & { comments?: ProjectByIdQuery['projectById']['comments'] | null };
}
const Comments_: React.FC<CommentsProps> = ({ project }) => {
  'use no memo';
  const translations = useTranslations();
  const loadingText = useT('loading...');
  const nothingMoreText = useT('nothing more to load', { swc: true });
  const currentUser = useContext(UserContext);
  const [editingCommentId, setEditingCommentId] = useState('');
  const { onOpenWithValue: openDeleteCommentModal, onClose: closeDeleteCommentModal, open: deleteCommentModalOpen, value: deleteCommentValue } = useOpen<Pick<Comment, 'id' | 'referenceIdentifier'>>();
  const projectId = project.id;
  const [addComment, { loading: addCommentLoading }] = useProjectAddCommentMutation();
  const [editComment] = useProjectEditCommentMutation();
  const { selectedAnnotations } = useCustomSelector((state) => state.rendererProvider, ['selectedAnnotations']);
  const { formatDate } = useFormatDate();
  const commentInputRef = useRef<CommentInputHandle>(null);

  const COMMENT_HEIGHT = 120;

  const commentsVariables = useMemo(() => {
    return { pagination: { limit: 10, cursor: null }, projectId, objectId: selectedAnnotations[0].identifier };
  }, [projectId, selectedAnnotations]);
  const {
    data: commentsData,
    fetchMore: fetchMoreComments,
    networkStatus,
  } = useProjectCommentsByObjectIdQuery({
    variables: commentsVariables,
  });
  const { data: comments, nextCursor: nextCursorComments, hasMore: hasMoreComments } = commentsData?.projectCommentsByObjectId || { data: [], nextCursor: null, hasMore: false };
  const commentsRowCount = comments?.length || 0;

  const isFetchingMoreComments = networkStatus === NetworkStatus.fetchMore;
  const commentsContainerRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasMoreComments ? commentsRowCount + 1 : commentsRowCount,
    getScrollElement: () => commentsContainerRef.current,
    estimateSize: () => COMMENT_HEIGHT,
  });

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= commentsRowCount - 1 && hasMoreComments && !isFetchingMoreComments) {
      fetchMoreComments({
        variables: {
          ...commentsVariables,
          pagination: { ...commentsVariables.pagination, cursor: nextCursorComments },
        },
      });
    }
  }, [comments.length, hasMoreComments, fetchMoreComments, nextCursorComments, isFetchingMoreComments, selectedAnnotations, projectId, commentsRowCount, commentsVariables, rowVirtualizer]);

  const onSubmitComment: FormikConfig<FormValues>['onSubmit'] = useCallback(
    (values, formikbag) => {
      if (values.comment === '' || values.comment === '<p></p>') {
        return;
      }
      const sanitizedComment = DOMPurify.sanitize(values.comment);
      addComment({
        variables: {
          projectId: projectId || '',
          comment: sanitizedComment,
          referenceIdentifier: selectedAnnotations[0].identifier,
        },
        refetchQueries: [{ query: PROJECT_COMMENTS, variables: commentsVariables }],
      }).then(() => {
        formikbag.resetForm();
        commentInputRef.current?.clear();
      });
    },
    [addComment, projectId, selectedAnnotations, commentsVariables],
  );

  const onEditComment: FormikConfig<FormValues>['onSubmit'] = useCallback(
    (values) => {
      const sanitizedComment = DOMPurify.sanitize(values.comment);
      if (sanitizedComment.trim() === '' || sanitizedComment.trim() === '<p></p>') return;
      editComment({
        variables: {
          projectId: projectId,
          commentId: editingCommentId,
          newComment: sanitizedComment,
        },
        optimisticResponse: {
          projectEditComment: {
            id: projectId,
            comments: [
              {
                __typename: 'Comment',
                id: editingCommentId,
                comment: sanitizedComment,
              },
            ],
          },
        },
        refetchQueries: [{ query: PROJECT_COMMENTS, variables: commentsVariables }],
      });
      setEditingCommentId('');
    },
    [editComment, projectId, editingCommentId, commentsVariables],
  );

  // A view-only guest reads the thread but cannot add to it.
  const canComment = useContext(ProjectAccessLevelContext) !== ProjectAccessLevel.View;

  return (
    <div className="flex flex-col h-full overflow-auto">
      {canComment && (
        <Formik initialValues={{ comment: '' }} onSubmit={onSubmitComment}>
          <Form className={classNames('mt-2 mr-2 p-1', comments?.length && 'border-b-2 pb-2')}>
            <CommentInput name="comment" placeholder={translations.commentPlaceholder} ref={commentInputRef} />
            <Button type="submit" className="my-2" variant="secondary" loading={addCommentLoading}>
              <T _str="add comment" swc />
            </Button>
          </Form>
        </Formik>
      )}
      <div className="flex flex-col overflow-auto" ref={commentsContainerRef}>
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
          className="divide-y"
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const isLoaderRow = virtualRow.index > commentsRowCount - 1;

            if (isLoaderRow) {
              return <div style={{ height: `${virtualRow.size}px` }}>{hasMoreComments ? <div>{loadingText}</div> : <div>{nothingMoreText}</div>}</div>;
            }

            const comment = comments[virtualRow.index];
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `100`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                className="p-2"
              >
                {comment.user && (
                  <div className="relative font-bold">
                    {comment.user.name}
                    {comment.user.id === currentUser.id && (
                      <>
                        <button className="absolute right-3" onClick={() => openDeleteCommentModal(comment)}>
                          <TrashIcon className="w-5 h-5 text-gray-500 hover:text-neon-green-600" />
                        </button>
                        <button className="absolute right-9" onClick={() => setEditingCommentId(comment.id)}>
                          <PencilIcon className="w-5 h-5 text-gray-500 hover:text-neon-green-600" />
                        </button>
                      </>
                    )}
                  </div>
                )}
                {!comment.updatedAt && <div className="text-xs text-gray-400">{formatDate(comment.createdAt)}</div>}
                {comment.updatedAt && <div className="text-xs text-gray-400">{formatDate(comment.createdAt) + ' (Edited)'}</div>}
                {editingCommentId !== comment.id && <div className="my-3 text-sm text-gray-500 break-words dark:text-gray-300" dangerouslySetInnerHTML={{ __html: comment.comment }} />}
                {editingCommentId === comment.id && (
                  <div>
                    <Formik initialValues={{ comment: comment.comment }} onSubmit={onEditComment}>
                      <Form className="mt-2 mr-2">
                        <CommentInput name="comment" placeholder={translations.commentPlaceholder} initialValue={comment.comment} />
                        <button type="submit" className="my-2">
                          <CheckIcon className="w-6 h-6 text-gray-300 hover:text-neon-green-600" />
                        </button>
                        <button type="button" onClick={() => setEditingCommentId('')} className="my-2">
                          <XMarkIcon className="w-6 h-6 text-gray-300 hover:text-error-red-500" />
                        </button>
                      </Form>
                    </Formik>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <ProjectDeleteCommentModal onClose={closeDeleteCommentModal} comment={deleteCommentValue} open={deleteCommentModalOpen} />
    </div>
  );
};

export const Comments = memo(Comments_);
